import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../services/supabaseClient';
import { Card } from '../ui/Card';
import {
  User,
  Plus,
  Trash2,
  ArrowUpRight,
  ArrowDownLeft,
  CheckCircle2,
  Clock,
  Wallet,
  X,
  Edit3,
  Landmark,
  ReceiptText,
  History,
  AlertCircle,
  Search,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  CircleDollarSign,
  BadgeCheck,
  CalendarDays,
  PiggyBank
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { differenceInDays, format } from 'date-fns';
import { canDeleteRecord } from './trackerUtils';

const todayDate = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(Number(value) || 0);

const createEmptyEntryForm = () => ({
  person_name: '',
  amount: '',
  notes: '',
  id: null,
  date: todayDate(),
  return_amount: '',
  return_date: todayDate()
});

const TRACKER_CONFIG = {
  lent: {
    label: 'Lent',
    table: 'lent',
    returnTable: 'lent_returns',
    foreignKey: 'lent_id',
    hasPerson: true,
    icon: User,
    partyLabel: 'Borrower',
    amountLabel: 'Amount lent',
    returnLabel: 'Return received',
    actionLabel: 'Record return',
    remainingLabel: 'Still receivable',
    totalLabel: 'Total lent',
    itemFallback: 'Borrower',
    accent: 'bg-[#EAF4FF] text-[#0077FF]'
  },
  safekeeping: {
    label: 'Borrowed',
    table: 'safe_keeping',
    returnTable: 'safe_returns',
    foreignKey: 'safe_id',
    hasPerson: true,
    icon: User,
    partyLabel: 'Owner',
    amountLabel: 'Amount borrowed',
    returnLabel: 'Amount returned',
    actionLabel: 'Record payment',
    remainingLabel: 'Still owed',
    totalLabel: 'Total borrowed',
    itemFallback: 'Borrower',
    accent: 'bg-[#FEF2F2] text-[#DC2626]'
  },
  savings: {
    label: 'Savings',
    table: 'savings',
    returnTable: 'savings_returns',
    foreignKey: 'saving_id',
    hasPerson: false,
    icon: Landmark,
    partyLabel: 'Goal',
    amountLabel: 'Saved amount',
    returnLabel: 'Withdrawal / used amount',
    actionLabel: 'Record usage',
    remainingLabel: 'Available savings',
    totalLabel: 'Total saved',
    itemFallback: 'Savings balance',
    accent: 'bg-[#ECFDF5] text-[#16A34A]'
  }
};

const TRACKER_TABS = ['lent', 'safekeeping', 'savings'];
const DISPLAY_TABS = [
  { value: 'lent', label: 'Lent', accent: 'bg-[#EAF4FF] text-[#0077FF]' },
  { value: 'safekeeping', label: 'Borrowed', accent: 'bg-[#FEF2F2] text-[#DC2626]' },
  { value: 'savings', label: 'Savings', accent: 'bg-[#ECFDF5] text-[#16A34A]' },
  { value: 'archive', label: 'Archive', accent: 'bg-[#F5F3FF] text-[#7C3AED]' }
];
const FILTER_OPTIONS = ['Outstanding', 'Partial', 'Settled', 'Overdue', 'Lent', 'Borrowed', 'Savings'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest', label: 'Highest amount' },
  { value: 'lowest', label: 'Lowest amount' },
  { value: 'due', label: 'Most due' },
  { value: 'updated', label: 'Recently updated' }
];

const getItemTitle = (item, config) => {
  if (config.hasPerson) return item.person_name || config.itemFallback;
  return item.notes || config.itemFallback;
};

const getStatusLabel = (item) => {
  if (item.isSettled || item.remaining <= 0) return 'Settled';
  if (item.progress > 0) return 'Partial';
  return 'Outstanding';
};

const getStatusMeta = (item) => {
  const label = getStatusLabel(item);
  if (label === 'Settled') {
    return { label, classes: 'bg-emerald-50 text-emerald-700 border border-emerald-100' };
  }
  if (label === 'Partial') {
    return { label, classes: 'bg-amber-50 text-amber-700 border border-amber-100' };
  }
  return { label, classes: 'bg-rose-50 text-rose-700 border border-rose-100' };
};

export default function MoneyTracker({ user }) {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const normalizedRequestedTab = requestedTab === 'borrowed' ? 'safekeeping' : requestedTab;
  const [activeTab, setActiveTab] = useState(TRACKER_TABS.includes(normalizedRequestedTab) ? normalizedRequestedTab : 'lent');
  const [recordsByTab, setRecordsByTab] = useState({ lent: [], safekeeping: [], savings: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [settlingId, setSettlingId] = useState(null);
  const [entryForm, setEntryForm] = useState(createEmptyEntryForm);
  const [partialAmount, setPartialAmount] = useState('');
  const [partialDate, setPartialDate] = useState(todayDate);
  const [returnDrafts, setReturnDrafts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortMode, setSortMode] = useState('newest');

  const refreshData = useCallback(async () => {
    if (!user) {
      setRecordsByTab({ lent: [], safekeeping: [], savings: [] });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextRecordsByTab = {};

      for (const tabKey of TRACKER_TABS) {
        const current = TRACKER_CONFIG[tabKey];

        const { data: records, error: recordsError } = await supabase
          .from(current.table)
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });

        if (recordsError) {
          throw new Error(`Failed to load ${current.label} data from ${current.table}: ${recordsError.message}`);
        }

        if (!records || records.length === 0) {
          nextRecordsByTab[tabKey] = [];
          continue;
        }

        const recordIds = records.map(record => record.id);
        const { data: returns, error: returnsError } = await supabase
          .from(current.returnTable)
          .select(`id, amount, date, ${current.foreignKey}`)
          .in(current.foreignKey, recordIds)
          .order('date', { ascending: false });

        if (returnsError) {
          throw new Error(`Failed to load ${current.label} return data from ${current.returnTable}: ${returnsError.message}`);
        }

        const returnsByRecordId = (returns || []).reduce((grouped, returnRecord) => {
          const parentId = returnRecord[current.foreignKey];
          if (!grouped[parentId]) grouped[parentId] = [];
          grouped[parentId].push({
            id: returnRecord.id,
            amount: returnRecord.amount,
            date: returnRecord.date
          });
          return grouped;
        }, {});

        nextRecordsByTab[tabKey] = records.map(record => {
          const itemReturns = returnsByRecordId[record.id] || [];
          const returned = itemReturns.reduce((sum, item) => sum + Number(item.amount || 0), 0);
          const totalAmount = Number(record.amount || 0);
          const remaining = totalAmount - returned;

          return {
            ...record,
            returns: itemReturns,
            total_returned: returned,
            remaining,
            progress: totalAmount > 0 ? Math.min((returned / totalAmount) * 100, 100) : 0,
            isSettled: remaining <= 0
          };
        });
      }

      setRecordsByTab(nextRecordsByTab);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (TRACKER_TABS.includes(normalizedRequestedTab)) setActiveTab(normalizedRequestedTab);
    if (searchParams.get('add') === '1') setShowAddModal(true);
  }, [normalizedRequestedTab, searchParams]);

  useEffect(() => {
    setSelectedItem(null);
    setSettlingId(null);
    setPartialAmount('');
    setPartialDate(todayDate());
    setReturnDrafts({});
    setEntryForm(createEmptyEntryForm());
  }, [activeTab]);

  const handleSaveEntry = async (event) => {
    event.preventDefault();
    const current = TRACKER_CONFIG[activeTab];
    const amount = Number(entryForm.amount);
    const openingReturnAmount = Number(entryForm.return_amount || 0);
    let savedEntryId = entryForm.id;
    let saveError = null;

    setError(null);

    if (!amount || amount <= 0) {
      setError(`Enter a ${current.amountLabel.toLowerCase()} greater than zero.`);
      return;
    }

    const payload = {
      user_id: user.id,
      amount,
      notes: entryForm.notes,
      date: entryForm.date || todayDate()
    };

    if (current.hasPerson) payload.person_name = entryForm.person_name;

    if (entryForm.id) {
      const { error: updateError } = await supabase
        .from(current.table)
        .update(payload)
        .eq('id', entryForm.id);

      saveError = updateError;
    } else if (openingReturnAmount > 0) {
      const { data: insertedEntry, error: insertError } = await supabase
        .from(current.table)
        .insert([payload])
        .select('id')
        .single();

      saveError = insertError;
      savedEntryId = insertedEntry?.id;
    } else {
      const { error: insertError } = await supabase
        .from(current.table)
        .insert([payload]);

      saveError = insertError;
    }

    if (saveError) {
      setError(`Failed to save ${current.label} entry to ${current.table}: ${saveError.message}`);
      return;
    }

    if (!entryForm.id && openingReturnAmount > 0 && savedEntryId) {
      const { error: returnInsertError } = await supabase.from(current.returnTable).insert([{
        [current.foreignKey]: savedEntryId,
        amount: openingReturnAmount,
        date: entryForm.return_date || payload.date
      }]);

      if (returnInsertError) {
        setError(`Entry saved, but failed to save ${current.returnLabel.toLowerCase()} in ${current.returnTable}: ${returnInsertError.message}`);
        refreshData();
        return;
      }
    }

    setEntryForm(createEmptyEntryForm());
    setShowAddModal(false);
    refreshData();
  };

  const handleEditParent = (item, event) => {
    event.stopPropagation();
    setEntryForm({
      id: item.id,
      person_name: item.person_name || '',
      amount: item.amount,
      notes: item.notes || '',
      date: item.date,
      return_amount: '',
      return_date: todayDate()
    });
    setShowAddModal(true);
  };

  const handleAddPartial = async (parentId, amountValue = partialAmount, dateValue = partialDate, closePanel = true) => {
    const current = TRACKER_CONFIG[activeTab];
    const amount = Number(amountValue);

    setError(null);

    if (!amount || amount <= 0) {
      setError(`Enter a ${current.returnLabel.toLowerCase()} greater than zero.`);
      return false;
    }

    const { error: insertError } = await supabase.from(current.returnTable).insert([{
      [current.foreignKey]: parentId,
      amount,
      date: dateValue || todayDate()
    }]);

    if (insertError) {
      setError(`Failed to save ${current.label} return to ${current.returnTable}: ${insertError.message}`);
      return false;
    }

    setPartialAmount('');
    setPartialDate(todayDate());
    setReturnDrafts(prev => {
      const next = { ...prev };
      delete next[parentId];
      return next;
    });
    if (closePanel) setSelectedItem(null);
    refreshData();
    return true;
  };

  const handleDelete = async (id) => {
    if (!selectedItem || !canDeleteRecord(selectedItem)) {
      setError('You can only delete records after the balance has been fully returned.');
      return;
    }

    if (window.confirm('Permanently delete this entire record?')) {
      const current = TRACKER_CONFIG[activeTab];
      setError(null);

      const { error: returnsDeleteError } = await supabase
        .from(current.returnTable)
        .delete()
        .eq(current.foreignKey, id);

      if (returnsDeleteError) {
        setError(`Failed to delete ${current.label} settlement history: ${returnsDeleteError.message}`);
        return;
      }

      const { error: deleteError } = await supabase.from(current.table).delete().eq('id', id);
      if (deleteError) {
        setError(`Failed to delete ${current.label} entry from ${current.table}: ${deleteError.message}`);
        return;
      }

      setSelectedItem(null);
      refreshData();
    }
  };

  const handleSettleTransaction = async (id) => {
    if (!id) return;

    const current = TRACKER_CONFIG[activeTab];
    setError(null);

    const { error: insertError } = await supabase.from(current.returnTable).insert([{
      [current.foreignKey]: id,
      amount: selectedItem.remaining,
      date: todayDate()
    }]);

    if (insertError) {
      setError(`Failed to settle ${current.label} entry in ${current.returnTable}: ${insertError.message}`);
      return;
    }

    setSelectedItem(null);
    setSettlingId(null);
    refreshData();
  };

  const archiveRecords = useMemo(() => {
    const combined = [
      ...(recordsByTab.lent || []),
      ...(recordsByTab.safekeeping || []),
      ...(recordsByTab.savings || [])
    ];
    return combined.filter(item => item.isSettled);
  }, [recordsByTab]);

  const data = useMemo(() => {
    const currentTabRecords = recordsByTab[activeTab] || [];
    const sourceRecords = activeTab === 'archive' ? archiveRecords : currentTabRecords;
    const query = searchQuery.trim().toLowerCase();

    const filtered = sourceRecords.filter(item => {
      const statusLabel = item.isSettled ? 'Settled' : item.remaining <= 0 ? 'Settled' : item.progress > 0 ? 'Partial' : 'Outstanding';
      const overdue = !item.isSettled && differenceInDays(new Date(), new Date(item.date)) > 30;
      const matchesQuery = !query || [
        item.person_name,
        item.notes,
        item.amount,
        item.remaining,
        item.date,
        statusLabel
      ].some(value => String(value || '').toLowerCase().includes(query));

      const matchesFilters = activeFilters.length === 0 || activeFilters.every(filter => {
        if (filter === 'Outstanding') return statusLabel === 'Outstanding';
        if (filter === 'Partial') return statusLabel === 'Partial';
        if (filter === 'Settled') return statusLabel === 'Settled';
        if (filter === 'Overdue') return overdue;
        if (filter === 'Lent') return activeTab === 'lent';
        if (filter === 'Borrowed') return activeTab === 'safekeeping';
        if (filter === 'Savings') return activeTab === 'savings';
        return false;
      });

      return matchesQuery && matchesFilters;
    });

    const sorted = [...filtered];
    switch (sortMode) {
      case 'oldest':
        sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
        break;
      case 'highest':
        sorted.sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
        break;
      case 'lowest':
        sorted.sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
        break;
      case 'due':
        sorted.sort((a, b) => Number(b.remaining || 0) - Number(a.remaining || 0));
        break;
      case 'updated':
        sorted.sort((a, b) => new Date(b.returns?.[0]?.date || b.date) - new Date(a.returns?.[0]?.date || a.date));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
    }

    return sorted;
  }, [activeTab, archiveRecords, recordsByTab, searchQuery, activeFilters, sortMode]);

  const globalTotalBalance = useMemo(() => {
    const allRecords = [
      ...(recordsByTab.lent || []),
      ...(recordsByTab.safekeeping || []),
      ...(recordsByTab.savings || [])
    ];
    return allRecords.reduce((sum, item) => sum + item.remaining, 0);
  }, [recordsByTab]);
  const globalTotalTracked = useMemo(() => {
    const allRecords = [
      ...(recordsByTab.lent || []),
      ...(recordsByTab.safekeeping || []),
      ...(recordsByTab.savings || [])
    ];
    return allRecords.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [recordsByTab]);
  const globalTotalReturned = useMemo(() => {
    const allRecords = [
      ...(recordsByTab.lent || []),
      ...(recordsByTab.safekeeping || []),
      ...(recordsByTab.savings || [])
    ];
    return allRecords.reduce((sum, item) => sum + item.total_returned, 0);
  }, [recordsByTab]);
  const summaryTotals = useMemo(() => {
    const lent = (recordsByTab.lent || []).reduce((acc, item) => ({
      amount: acc.amount + Number(item.amount || 0),
      remaining: acc.remaining + Number(item.remaining || 0),
      active: acc.active + 1,
      settled: acc.settled + (item.isSettled ? 1 : 0)
    }), { amount: 0, remaining: 0, active: 0, settled: 0 });
    const borrowed = (recordsByTab.safekeeping || []).reduce((acc, item) => ({
      amount: acc.amount + Number(item.amount || 0),
      remaining: acc.remaining + Number(item.remaining || 0),
      active: acc.active + 1,
      settled: acc.settled + (item.isSettled ? 1 : 0)
    }), { amount: 0, remaining: 0, active: 0, settled: 0 });
    const savings = (recordsByTab.savings || []).reduce((acc, item) => ({
      amount: acc.amount + Number(item.amount || 0),
      remaining: acc.remaining + Number(item.remaining || 0),
      active: acc.active + 1,
      settled: acc.settled + (item.isSettled ? 1 : 0)
    }), { amount: 0, remaining: 0, active: 0, settled: 0 });

    return {
      lent,
      borrowed,
      savings,
      outstanding: lent.remaining + borrowed.remaining,
      activeRecords: lent.active + borrowed.active + savings.active,
      netPosition: lent.amount - borrowed.amount + savings.amount
    };
  }, [recordsByTab]);
  const currentConfig = TRACKER_CONFIG[activeTab === 'archive' ? 'lent' : activeTab] || TRACKER_CONFIG.lent;
  const ActiveIcon = currentConfig.icon;
  const activeTabLabel = DISPLAY_TABS.find(tab => tab.value === activeTab)?.label || 'Lent';
  const quickAlerts = useMemo(() => {
    const checklist = [];
    const lentItems = (recordsByTab.lent || []).filter(item => !item.isSettled && item.remaining > 0);
    const borrowedItems = (recordsByTab.safekeeping || []).filter(item => !item.isSettled && item.remaining > 0);
    const overdueItems = [...lentItems, ...borrowedItems].filter(item => differenceInDays(new Date(), new Date(item.date)) > 30);
    const latestSettlement = [...(recordsByTab.lent || []), ...(recordsByTab.safekeeping || []), ...(recordsByTab.savings || [])]
      .filter(item => item.returns?.length)
      .sort((a, b) => new Date(b.returns?.[0]?.date || b.date) - new Date(a.returns?.[0]?.date || a.date))[0];

    if (lentItems.length > 0) {
      const topItem = lentItems.sort((a, b) => Number(b.remaining || 0) - Number(a.remaining || 0))[0];
      checklist.push({
        title: `${getItemTitle(topItem, TRACKER_CONFIG.lent)} owes you`,
        detail: `${formatCurrency(topItem.remaining)} still outstanding`,
        icon: ArrowDownLeft,
        tone: 'bg-[#ECFDF5] text-[#16A34A]'
      });
    }

    if (borrowedItems.length > 0) {
      const topItem = borrowedItems.sort((a, b) => Number(b.remaining || 0) - Number(a.remaining || 0))[0];
      checklist.push({
        title: `You owe ${getItemTitle(topItem, TRACKER_CONFIG.safekeeping)}`,
        detail: `${formatCurrency(topItem.remaining)} is still pending`,
        icon: ArrowUpRight,
        tone: 'bg-[#FEF2F2] text-[#DC2626]'
      });
    }

    if ((recordsByTab.savings || []).length > 0) {
      const currentMonth = (recordsByTab.savings || []).filter(item => format(new Date(item.date), 'yyyy-MM') === format(new Date(), 'yyyy-MM'));
      checklist.push({
        title: 'Savings momentum is building',
        detail: `${currentMonth.length} contributions this month`,
        icon: PiggyBank,
        tone: 'bg-[#EAF4FF] text-[#0077FF]'
      });
    }

    if (overdueItems.length > 0) {
      checklist.push({
        title: `${overdueItems.length} records need attention`,
        detail: 'These balances have been pending for over 30 days',
        icon: Clock,
        tone: 'bg-[#FFF7ED] text-[#EA580C]'
      });
    }

    if (latestSettlement) {
      checklist.push({
        title: 'Recent settlement captured',
        detail: `${getItemTitle(latestSettlement, TRACKER_CONFIG[latestSettlement.person_name ? 'lent' : 'savings'])} was updated recently`,
        icon: BadgeCheck,
        tone: 'bg-[#F5F3FF] text-[#7C3AED]'
      });
    }

    return checklist.slice(0, 4);
  }, [recordsByTab]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-24 font-sans text-black">
      <header className="space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#EAF4FF] text-[#0077FF]">
              <Wallet size={22} strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#71717A]">Tracker ledger</p>
              <h1 className="mt-2 truncate text-3xl font-black tracking-tight text-black md:text-4xl">Money Tracker</h1>
              <p className="mt-2 truncate text-sm font-bold text-[#71717A]">{currentConfig.label} records</p>
            </div>
          </div>

          <button
            onClick={() => { setEntryForm(createEmptyEntryForm()); setShowAddModal(true); }}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-black px-5 text-sm font-black text-white transition active:scale-[0.98] md:w-auto"
          >
            <Plus size={17} strokeWidth={1.8} /> New Entry
          </button>
        </div>

        <Card className="overflow-hidden border border-[#F0F0F0] bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1D4ED8] p-0 text-white shadow-sm">
          <div className="flex flex-col gap-5 p-5 md:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-white/70">Financial summary hero</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight">Your money position at a glance</h2>
              </div>
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 text-white">
                <Sparkles size={18} strokeWidth={1.8} />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Total lent</p>
                <p className="mt-2 text-xl font-black">{formatCurrency(summaryTotals.lent.amount)}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Total borrowed</p>
                <p className="mt-2 text-xl font-black">{formatCurrency(summaryTotals.borrowed.amount)}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Total savings</p>
                <p className="mt-2 text-xl font-black">{formatCurrency(summaryTotals.savings.amount)}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Net position</p>
                <p className="mt-2 text-xl font-black">{formatCurrency(summaryTotals.netPosition)}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Outstanding</p>
                <p className="mt-2 text-xl font-black">{formatCurrency(summaryTotals.outstanding)}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Active records</p>
                <p className="mt-2 text-xl font-black">{summaryTotals.activeRecords}</p>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Card className="border border-[#F0F0F0] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Money lent</p>
                <p className="mt-2 text-xl font-black text-black">{formatCurrency(summaryTotals.lent.remaining)}</p>
              </div>
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-[#EAF4FF] text-[#0077FF]">
                <ArrowDownLeft size={18} strokeWidth={1.8} />
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-[#71717A]">{summaryTotals.lent.active} active records</p>
          </Card>
          <Card className="border border-[#F0F0F0] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Money borrowed</p>
                <p className="mt-2 text-xl font-black text-black">{formatCurrency(summaryTotals.borrowed.remaining)}</p>
              </div>
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-[#FEF2F2] text-[#DC2626]">
                <ArrowUpRight size={18} strokeWidth={1.8} />
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-[#71717A]">{summaryTotals.borrowed.active} active records</p>
          </Card>
          <Card className="border border-[#F0F0F0] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Savings</p>
                <p className="mt-2 text-xl font-black text-black">{formatCurrency(summaryTotals.savings.remaining)}</p>
              </div>
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-[#ECFDF5] text-[#16A34A]">
                <PiggyBank size={18} strokeWidth={1.8} />
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-[#71717A]">{summaryTotals.savings.active} active goals</p>
          </Card>
          <Card className="border border-[#F0F0F0] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Outstanding</p>
                <p className="mt-2 text-xl font-black text-black">{formatCurrency(summaryTotals.outstanding)}</p>
              </div>
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-[#FFF7ED] text-[#EA580C]">
                <CircleDollarSign size={18} strokeWidth={1.8} />
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-[#71717A]">Need attention soon</p>
          </Card>
        </div>
      </header>

      <div className="rounded-[28px] border border-[#F0F0F0] bg-white p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#71717A]">Workspace views</p>
            <h3 className="mt-1 text-lg font-black tracking-tight text-black">{activeTabLabel}</h3>
          </div>
          <div className="rounded-full bg-[#F8F8F8] px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-[#71717A]">Last tab remembered</div>
        </div>
        <div className="grid gap-2 md:grid-cols-4">
          {DISPLAY_TABS.map(tab => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`rounded-2xl px-3 py-3 text-center text-[11px] font-black uppercase tracking-[0.16em] transition ${isActive ? 'bg-black text-white shadow-sm' : 'bg-[#F8F8F8] text-[#71717A] hover:bg-[#F4F4F5] hover:text-black'}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-[28px] border border-[#F0F0F0] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-3 rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] px-3 py-2">
            <Search size={16} className="text-[#71717A]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by person, amount, note or date"
              className="w-full bg-transparent text-sm font-semibold text-black outline-none placeholder:text-[#A1A1AA]"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] px-3 py-2 text-sm font-semibold text-[#71717A]">
              <SlidersHorizontal size={15} /> Filters
            </div>
            {FILTER_OPTIONS.map(filter => {
              const isActive = activeFilters.includes(filter);
              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilters(prev => prev.includes(filter) ? prev.filter(item => item !== filter) : [...prev, filter])}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] transition ${isActive ? 'bg-black text-white' : 'bg-[#F8F8F8] text-[#71717A] hover:bg-[#F4F4F5] hover:text-black'}`}
                >
                  {filter}
                </button>
              );
            })}
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              className="rounded-full border border-[#F0F0F0] bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-[#71717A] outline-none"
            >
              {SORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-[1.4fr_0.6fr]">
        <div className="rounded-[28px] border border-[#F0F0F0] bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#71717A]">Smart alerts</p>
              <h3 className="mt-1 text-lg font-black tracking-tight text-black">Recommended actions</h3>
            </div>
            <div className="rounded-full bg-[#F8F8F8] px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-[#71717A]">Live insights</div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {quickAlerts.map(alert => {
              const Icon = alert.icon;
              return (
                <div key={alert.title} className={`rounded-2xl border border-[#F0F0F0] p-3 ${alert.tone}`}>
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/70">
                      <Icon size={16} strokeWidth={1.8} />
                    </span>
                    <p className="text-sm font-black">{alert.title}</p>
                  </div>
                  <p className="mt-2 text-sm font-semibold opacity-80">{alert.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-[28px] border border-[#F0F0F0] bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] text-[#71717A]">
            <TrendingUp size={14} /> Money flow
          </div>
          <div className="mt-3 space-y-3">
            <div className="rounded-2xl bg-[#F8F8F8] p-3">
              <p className="text-sm font-semibold text-[#71717A]">Tracked amount</p>
              <p className="mt-1 text-2xl font-black text-black">{formatCurrency(globalTotalTracked)}</p>
            </div>
            <div className="rounded-2xl bg-[#F8F8F8] p-3">
              <p className="text-sm font-semibold text-[#71717A]">Settled so far</p>
              <p className="mt-1 text-2xl font-black text-emerald-600">{formatCurrency(globalTotalReturned)}</p>
            </div>
            <div className="rounded-2xl bg-[#F8F8F8] p-3">
              <p className="text-sm font-semibold text-[#71717A]">Remaining balance</p>
              <p className="mt-1 text-2xl font-black text-black">{formatCurrency(globalTotalBalance)}</p>
            </div>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {error && (
          <div className="col-span-full rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <p className="flex items-center gap-2 text-sm font-black text-rose-600">
              <AlertCircle size={17} strokeWidth={1.8} /> {error}
            </p>
          </div>
        )}

        {loading ? (
          <div className="col-span-full rounded-3xl border border-dashed border-[#F0F0F0] bg-white px-6 py-20 text-center text-sm font-black uppercase tracking-[0.2em] text-[#71717A]">
            Syncing records
          </div>
        ) : error ? null : data.length === 0 ? (
          <div className="col-span-full rounded-3xl border border-dashed border-[#F0F0F0] bg-white px-6 py-20 text-center">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-[#71717A]">No records</p>
            <button
              onClick={() => { setEntryForm(createEmptyEntryForm()); setShowAddModal(true); }}
              className="mx-auto mt-5 flex h-12 items-center justify-center gap-2 rounded-xl bg-[#0077FF] px-5 text-sm font-black text-white transition active:scale-[0.98]"
            >
              <Plus size={17} strokeWidth={1.8} /> Create Entry
            </button>
          </div>
        ) : data.map(item => {
          const statusMeta = getStatusMeta(item);
          const isOverdue = !item.isSettled && differenceInDays(new Date(), new Date(item.date)) > 30;
          return (
            <Card
              key={item.id}
              className="group flex min-h-[360px] cursor-pointer flex-col border border-[#F0F0F0] bg-white p-5 transition hover:border-[#D4D4D8]"
              onClick={() => setSelectedItem(item)}
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${currentConfig.accent}`}>
                  <ActiveIcon size={19} strokeWidth={1.8} />
                </span>
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    onClick={(event) => handleEditParent(item, event)}
                    className="grid h-10 w-10 place-items-center rounded-xl text-[#A1A1AA] transition hover:bg-[#F8F8F8] hover:text-black"
                    aria-label={`Edit ${getItemTitle(item, currentConfig)}`}
                  >
                    <Edit3 size={16} strokeWidth={1.8} />
                  </button>
                  <span className={`flex h-9 items-center gap-1 rounded-xl px-3 text-[10px] font-black uppercase tracking-[0.16em] ${statusMeta.classes}`}>
                    {item.isSettled ? <CheckCircle2 size={13} strokeWidth={1.8} /> : <Clock size={13} strokeWidth={1.8} />}
                    {statusMeta.label}
                  </span>
                </div>
              </div>

              <div className="min-w-0">
                <h3 className="truncate text-xl font-black tracking-tight text-black">{getItemTitle(item, currentConfig)}</h3>
                <p className="mt-1 truncate text-[11px] font-bold uppercase tracking-tight text-[#71717A]">{item.date}</p>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-[#F0F0F0] bg-white p-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#71717A]">Original</p>
                  <p className="mt-2 text-base font-black text-black">{formatCurrency(item.amount)}</p>
                </div>
                <div className="rounded-2xl border border-[#F0F0F0] bg-white p-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#71717A]">Returned</p>
                  <p className="mt-2 text-base font-black text-emerald-600">{formatCurrency(item.total_returned)}</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-end justify-between gap-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Remaining</p>
                  <p className="shrink-0 text-lg font-black text-black">{formatCurrency(item.remaining)}</p>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-[#F4F4F5]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.progress}%` }}
                    className={`h-full rounded-full ${item.isSettled ? 'bg-emerald-500' : 'bg-[#0077FF]'}`}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold text-[#71717A]">
                <span className="flex items-center gap-1 rounded-full bg-[#F8F8F8] px-2.5 py-1.5">
                  <CalendarDays size={12} /> {format(new Date(item.date), 'MMM d')}
                </span>
                {isOverdue && <span className="rounded-full bg-[#FFF7ED] px-2.5 py-1.5 text-[#EA580C]">Overdue</span>}
              </div>

              {!item.isSettled && (
                <form
                  onClick={(event) => event.stopPropagation()}
                  onSubmit={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleAddPartial(item.id, returnDrafts[item.id], todayDate(), false);
                  }}
                  className="mt-auto rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] p-3"
                >
                  <label className="ml-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#71717A]">{currentConfig.returnLabel}</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      className="h-11 min-w-0 flex-1 rounded-xl border border-[#F0F0F0] bg-white px-3 text-sm font-bold text-black outline-none transition focus:border-black"
                      value={returnDrafts[item.id] || ''}
                      onChange={(event) => setReturnDrafts(prev => ({ ...prev, [item.id]: event.target.value }))}
                    />
                    <button type="submit" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-black text-white transition active:scale-[0.96]" aria-label={currentConfig.actionLabel}>
                      <Plus size={16} strokeWidth={1.9} />
                    </button>
                  </div>
                </form>
              )}
            </Card>
          );
        })}
      </section>

      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 z-[100] flex items-center justify-end bg-black/25 backdrop-blur-sm">
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="flex h-screen w-full max-w-md flex-col overflow-y-auto border-l border-[#F0F0F0] bg-white p-5 md:p-7"
            >
              <div className="mb-6 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] text-[#71717A]">
                    <History size={14} strokeWidth={1.8} /> Statement
                  </p>
                  <h2 className="mt-2 truncate text-2xl font-black tracking-tight text-black">{getItemTitle(selectedItem, currentConfig)}</h2>
                </div>
                <button
                  onClick={() => { setSelectedItem(null); setSettlingId(null); }}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#D4D4D8]"
                  aria-label="Close statement"
                >
                  <X size={20} strokeWidth={1.8} />
                </button>
              </div>

              <div className="flex-1 space-y-5">
                <div className="rounded-3xl bg-black p-6 text-white">
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-white/50">{currentConfig.remainingLabel}</p>
                  <p className="mt-3 text-4xl font-black tracking-tight">{formatCurrency(selectedItem.remaining)}</p>
                </div>

                {!selectedItem.isSettled && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleAddPartial(selectedItem.id);
                    }}
                    className="rounded-2xl border border-[#F0F0F0] bg-white p-4"
                  >
                    <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-[#71717A]">{currentConfig.returnLabel}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        className="h-12 rounded-xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                        value={partialAmount}
                        onChange={(event) => setPartialAmount(event.target.value)}
                      />
                      <input
                        type="date"
                        className="h-12 rounded-xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                        value={partialDate}
                        onChange={(event) => setPartialDate(event.target.value)}
                      />
                    </div>
                    <button type="submit" className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#0077FF] px-5 text-sm font-black text-white transition active:scale-[0.98]">
                      <Plus size={16} strokeWidth={1.9} /> {currentConfig.actionLabel}
                    </button>
                  </form>
                )}

                {!selectedItem.isSettled && selectedItem.remaining > 0 && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                    <p className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
                      <CheckCircle2 size={14} strokeWidth={1.8} /> Mark as settled
                    </p>
                    {settlingId === selectedItem.id ? (
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-emerald-700">Confirm final settlement of {formatCurrency(selectedItem.remaining)}.</p>
                        <button
                          onClick={() => handleSettleTransaction(selectedItem.id)}
                          className="h-11 w-full rounded-xl bg-emerald-600 px-4 text-sm font-black text-white transition active:scale-[0.98]"
                        >
                          Confirm Settlement
                        </button>
                        <button
                          onClick={() => setSettlingId(null)}
                          className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm font-black text-emerald-700 transition active:scale-[0.98]"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSettlingId(selectedItem.id)}
                        className="h-11 w-full rounded-xl bg-emerald-600 px-4 text-sm font-black text-white transition active:scale-[0.98]"
                      >
                        Settle Transaction
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#71717A]">
                    <ReceiptText size={14} strokeWidth={1.8} /> Activity Log
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-4 rounded-2xl border border-[#F0F0F0] bg-white p-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-black">{currentConfig.amountLabel}</p>
                        <p className="mt-1 truncate text-[11px] font-bold uppercase tracking-tight text-[#71717A]">{selectedItem.date}</p>
                      </div>
                      <p className="shrink-0 text-sm font-black text-black">{formatCurrency(selectedItem.amount)}</p>
                    </div>
                    {(selectedItem.returns || []).map((record, index) => (
                      <div key={record.id || index} className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-emerald-700">{currentConfig.returnLabel}</p>
                          <p className="mt-1 truncate text-[11px] font-bold uppercase tracking-tight text-emerald-600">{record.date}</p>
                        </div>
                        <p className="shrink-0 text-sm font-black text-emerald-700">- {formatCurrency(record.amount)}</p>
                      </div>
                    ))}
                    {selectedItem.isSettled && (
                      <div className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm font-black text-emerald-700">Settled</p>
                        <CheckCircle2 size={20} strokeWidth={1.8} className="text-emerald-600" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {canDeleteRecord(selectedItem) ? (
                <button
                  onClick={() => handleDelete(selectedItem.id)}
                  className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-rose-100 bg-white px-4 text-sm font-black text-rose-600 transition hover:bg-rose-50 active:scale-[0.98]"
                >
                  <Trash2 size={16} strokeWidth={1.8} /> Delete Record
                </button>
              ) : (
                <div className="mt-5 rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] p-3 text-center text-sm font-semibold text-[#71717A]">
                  Full settlement is required before this record can be deleted.
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/35 p-4 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-[#F0F0F0] bg-white p-5 md:p-7"
            >
              <div className="mb-6 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#71717A]">{entryForm.id ? 'Edit record' : 'New record'}</p>
                  <h2 className="mt-2 truncate text-2xl font-black tracking-tight text-black">{currentConfig.label}</h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#D4D4D8]"
                  aria-label="Close form"
                >
                  <X size={20} strokeWidth={1.8} />
                </button>
              </div>

              <form onSubmit={handleSaveEntry} className="space-y-4">
                {currentConfig.hasPerson && (
                  <div className="space-y-1">
                    <label className="ml-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">{currentConfig.partyLabel}</label>
                    <input
                      type="text"
                      required
                      placeholder="Name"
                      className="h-12 w-full rounded-2xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                      value={entryForm.person_name}
                      onChange={(event) => setEntryForm({ ...entryForm, person_name: event.target.value })}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                  <div className="space-y-1">
                    <label className="ml-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">{currentConfig.amountLabel}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      placeholder="0"
                      className="h-12 w-full rounded-2xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                      value={entryForm.amount}
                      onChange={(event) => setEntryForm({ ...entryForm, amount: event.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="ml-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">Date</label>
                    <input
                      type="date"
                      required
                      className="h-12 w-full rounded-2xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                      value={entryForm.date}
                      onChange={(event) => setEntryForm({ ...entryForm, date: event.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="ml-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#71717A]">
                    {currentConfig.hasPerson ? 'Description' : 'Savings label'}
                  </label>
                  <input
                    type="text"
                    placeholder={currentConfig.hasPerson ? 'Short note' : 'Emergency fund, trip, goal'}
                    className="h-12 w-full rounded-2xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                    value={entryForm.notes}
                    onChange={(event) => setEntryForm({ ...entryForm, notes: event.target.value })}
                  />
                </div>

                {!entryForm.id && (
                  <div className="rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] p-4">
                    <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-[#71717A]">{currentConfig.returnLabel}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        className="h-12 rounded-xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                        value={entryForm.return_amount}
                        onChange={(event) => setEntryForm({ ...entryForm, return_amount: event.target.value })}
                      />
                      <input
                        type="date"
                        className="h-12 rounded-xl border border-[#F0F0F0] bg-white px-4 text-sm font-bold text-black outline-none transition focus:border-black"
                        value={entryForm.return_date}
                        onChange={(event) => setEntryForm({ ...entryForm, return_date: event.target.value })}
                      />
                    </div>
                  </div>
                )}

                <button type="submit" className="flex h-12 w-full items-center justify-center rounded-xl bg-black px-5 text-sm font-black text-white transition active:scale-[0.98]">
                  {entryForm.id ? 'Update Record' : 'Save Record'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
